import { BillingHelper } from "@/Billing/Helpers/BillingHelper";
import { BillingStore } from "@/Billing/Stores/BillingStore";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";

class SalesSettingsContainerHandler extends BaseOperationHandler {
    private readonly helper: MyleHelper;

    constructor() {
        super();

        this.helper = new MyleHelper();

        this.RegisterOperation(LocalOperations.Billing.Sales_SearchSettings_ShowOptionsButtonClick, this.DisplayMyNotesSettings);
        this.RegisterOperation(LocalOperations.Billing.Sales_OnSettingsContainerCheckboxChange, this.OnSettingsContainerCheckboxChange);
        this.RegisterOperation(LocalOperations.Misc.MyleApp_Container_Click, this.CloseSearchFilterPopup, false);
        this.RegisterOperation(LocalOperations.Billing.Sales_SearchSettings_ProviderFilterTextChanged, this.ProviderFilterChanged);
        this.RegisterOperation(LocalOperations.Billing.Sales_SearchSettings_BilledByFilterTextChanged, this.BilledByFilterChanged);
        this.RegisterOperation(LocalOperations.Billing.Sales_SearchSettings_FromDateChanged, this.FromDateChanged);
        this.RegisterOperation(LocalOperations.Billing.Sales_SearchSettings_ToDateChanged, this.ToDateChanged);
        this.RegisterOperation(LocalOperations.Billing.Sales_SearchSettings_OkClick, this.OnOkClick);
        this.RegisterOperation(LocalOperations.Billing.Sales_SearchSettings_CancelClick, this.OnCancelClick);

        this.RegisterOperation(LocalOperations.Billing.Sales_SearchSettings_ColumnOrderingPopup_Open, this.ShowColumnOrderingPopup);
        this.RegisterOperation(LocalOperations.Billing.Sales_SearchSettings_ColumnOrderingPopup_Close, this.CloseColumnOrderingPopup);

        this.RegisterOperation(LocalOperations.Billing.Sales_SearchSettings_VisitFromDateChanged, this.VisitFromDateChanged);
        this.RegisterOperation(LocalOperations.Billing.Sales_SearchSettings_VisitToDateChanged, this.VisitToDateChanged);
    }

    private ShowColumnOrderingPopup(request: LocalEvent, store: BillingStore): void {
        store.SalesInboxModel = store.SalesInboxModel.set("ColumnOrderingPopupOpen", true);
        store.SalesInboxModel = store.SalesInboxModel.set("ShowSalesDisplaySettings", false);
        sessionStore.showGlobalOverlay(true);
    }

    private CloseColumnOrderingPopup(request: LocalEvent, store: BillingStore): void {
        store.SalesInboxModel = store.SalesInboxModel.set("ColumnOrderingPopupOpen", false);
        store.SalesInboxModel = store.SalesInboxModel.set("ShowSalesDisplaySettings", false);
        sessionStore.hideGlobalOverlay();
    }

    private FromDateChanged(request: LocalEvent, store: BillingStore): void {
        const nextFromDate = request.Get<Date>("value");
        const model = store.SalesInboxModel;
        store.SalesInboxModel = store.SalesInboxModel
            .set("DateFrom", nextFromDate)
            .set("ValidationError", ValidationHelper.validateSearchDateRangeMax5Years(nextFromDate, model.get("DateTo")));
    }

    private ToDateChanged(request: LocalEvent, store: BillingStore): void {
        const nextToDate = request.Get<Date>("value");
        const model = store.SalesInboxModel;
        store.SalesInboxModel = store.SalesInboxModel
            .set("DateTo", nextToDate)
            .set("ValidationError", ValidationHelper.validateSearchDateRangeMax5Years(model.get("DateFrom"), nextToDate));
    }

    private OnSettingsContainerCheckboxChange(request: LocalEvent, store: BillingStore): void {
        var target = request.Get<CheckboxTarget>("target");
        var checked = request.Get<boolean>("checked");
        var id = request.Get<string>("id");

        switch (target) {
            case CheckboxTarget.Myle_Billing_Sales_Sites:
                store.SalesInboxModel = store.SalesInboxModel.set("Sites", store.helper.FindUpdateICheckableItem<SiteUIModel>(store.SalesInboxModel.get("Sites"), id, checked));
                store.SalesInboxModel = store.SalesInboxModel.set("ProviderFiltered", store.getSalesProviderBasedOnSitesAndQuery());
                store.SalesInboxModel = store.SalesInboxModel.set("BilledByFiltered", store.getSalesBilledByBasedOnSitesAndQuery());
                break;

            case CheckboxTarget.Myle_Billing_Sales_Provider:
                store.SalesInboxModel = store.SalesInboxModel.set("ProviderGlobalCache", this.helper.FindUpdateICheckableItem<EmployeeUIModel>(store.SalesInboxModel.get("ProviderGlobalCache"), id, checked));
                store.SalesInboxModel = store.SalesInboxModel.set("ProviderFiltered", store.getSalesProviderBasedOnSitesAndQuery());
                break;

            case CheckboxTarget.Myle_Billing_Sales_BilledBy:
                store.SalesInboxModel = store.SalesInboxModel.set("BilledByGlobalCache", this.helper.FindUpdateICheckableItem<EmployeeUIModel>(store.SalesInboxModel.get("BilledByGlobalCache"), id, checked));
                store.SalesInboxModel = store.SalesInboxModel.set("BilledByFiltered", store.getSalesBilledByBasedOnSitesAndQuery());
                break;

            case CheckboxTarget.Myle_Billing_Sales_SitesControlCheck:
                store.SalesInboxModel = store.SalesInboxModel.set("SelectAllSitesChecked", checked);
                var sites = store.helper.ChangeCheckStateFromAllICheckable(store.SalesInboxModel.get("Sites"), checked);
                store.SalesInboxModel = store.SalesInboxModel.set("Sites", sites);
                store.SalesInboxModel = store.SalesInboxModel.set("ProviderFiltered", store.getSalesProviderBasedOnSitesAndQuery());
                store.SalesInboxModel = store.SalesInboxModel.set("BilledByFiltered", store.getSalesBilledByBasedOnSitesAndQuery());
                break;

            case CheckboxTarget.Myle_Billing_Sales_ProviderControlCheck:
                store.SalesInboxModel = store.SalesInboxModel.set("SelectAllProviderChecked", checked);
                var list = store.getSalesProviderBasedOnSitesAndQuery();
                var listOfClinicianIds = list.toArray().map(i => i.Id);
                var c = store.helper.FindUpdateICheckableItems(store.SalesInboxModel.get("ProviderGlobalCache"), listOfClinicianIds, checked);
                store.SalesInboxModel = store.SalesInboxModel.set("ProviderGlobalCache", c);
                var checkedList = store.getSalesProviderBasedOnSitesAndQuery();
                store.SalesInboxModel = store.SalesInboxModel.set("ProviderFiltered", checkedList);
                break;

            case CheckboxTarget.Myle_Billing_Sales_BilledByControlCheck:
                store.SalesInboxModel = store.SalesInboxModel.set("SelectAllBilledByChecked", checked);
                var list = store.getSalesBilledByBasedOnSitesAndQuery();
                var listOfClinicianIds = list.toArray().map(i => i.Id);
                var c = store.helper.FindUpdateICheckableItems(store.SalesInboxModel.get("BilledByGlobalCache"), listOfClinicianIds, checked);
                store.SalesInboxModel = store.SalesInboxModel.set("BilledByGlobalCache", c);
                var checkedList = store.getSalesBilledByBasedOnSitesAndQuery();
                store.SalesInboxModel = store.SalesInboxModel.set("BilledByFiltered", checkedList);
                break;

            case CheckboxTarget.Myle_Billing_Sales_Status:
                store.SalesInboxModel = store.SalesInboxModel.set("Status", store.helper.FindUpdateICheckableItem<CheckboxUIModel>(store.SalesInboxModel.get("Status"), id, checked));
                break;

            case CheckboxTarget.Myle_Billing_Sales_StatusControlCheck:
                store.SalesInboxModel = store.SalesInboxModel.set("SelectAllStatusChecked", checked);
                store.SalesInboxModel = store.SalesInboxModel.set("Status", this.helper.ChangeCheckStateFromAllICheckable(store.SalesInboxModel.get("Status"), checked));
                break;

            case CheckboxTarget.Myle_Billing_Sales_PaymentMethod:
                store.SalesInboxModel = store.SalesInboxModel.set("PaymentMethod", store.helper.FindUpdateICheckableItem<CheckboxUIModel>(store.SalesInboxModel.get("PaymentMethod"), id, checked));
                break;

            case CheckboxTarget.Myle_Billing_Sales_PaymentsControlCheck:
                store.SalesInboxModel = store.SalesInboxModel.set("SelectAllPaymentChecked", checked);
                store.SalesInboxModel = store.SalesInboxModel.set("PaymentMethod", this.helper.ChangeCheckStateFromAllICheckable(store.SalesInboxModel.get("PaymentMethod"), checked));
                break;

            case CheckboxTarget.Myle_Billing_Sales_OnlySelectedSites:
                store.SalesInboxModel = store.SalesInboxModel.set("ShowOnlySelectedSites", checked);
                break;

            case CheckboxTarget.Myle_Billing_Settings_ValidityAllChecked:
                store.SalesInboxModel = store.SalesInboxModel.set("SelectAllValidityChecked", checked);
                store.SalesInboxModel = store.SalesInboxModel.set("Validity", this.helper.ChangeCheckStateFromAllICheckable(store.SalesInboxModel.get("Validity"), checked));
                break;

            case CheckboxTarget.Myle_Billing_Settings_Validity:
                store.SalesInboxModel = store.SalesInboxModel.set("Validity", this.helper.FindUpdateICheckableItem<CheckboxUIModel>(store.SalesInboxModel.get("Validity"), id, checked));
                break;
        }
    }

    private DisplayMyNotesSettings(request: LocalEvent, store: BillingStore): void {
        var open = store.SalesInboxModel.get("ShowSalesDisplaySettings");
        store.SalesInboxModel = store.SalesInboxModel.set("ShowSalesDisplaySettings", !open);
    }

    private OnCancelClick(request: LocalEvent, store: BillingStore): void {
        const model = store.SalesInboxModel = store.SalesInboxModel.set("ShowSalesDisplaySettings", false);

        const validationError = ValidationHelper.validateSearchDateRangeMax5Years(model.get("DateFrom"), model.get("DateTo"));

        if (validationError !== null) {
            store.SalesInboxModel = store.SalesInboxModel
                .set("DateFrom", BillingHelper.GetSearchDefaultFromDate())
                .set("DateTo", BillingHelper.GetSearchDefaultToDate())
                .set("ValidationError", null);
        }
    }

    private OnOkClick(request: LocalEvent, store: BillingStore): void {
        store.SalesInboxModel = store.SalesInboxModel.set("ShowSalesDisplaySettings", false);

        store.BillingAction.LoadSalesData(store.SalesInboxModel);
    }

    private CloseSearchFilterPopup(request: LocalEvent, store: BillingStore): void {
        if (store.SalesInboxModel.get("ShowSalesDisplaySettings") === true) {
            this.OnOkClick(request, store);
            store.EmitChangeToListeners();
        }
    }

    private ProviderFilterChanged(request: LocalEvent, store: BillingStore): void {
        store.SalesInboxModel = store.SalesInboxModel.set("ProviderSearchText", request.Get<string>("value"));
        store.SalesInboxModel = store.SalesInboxModel.set("ProviderFiltered", store.getSalesProviderBasedOnSitesAndQuery());
    }

    private BilledByFilterChanged(request: LocalEvent, store: BillingStore): void {
        store.SalesInboxModel = store.SalesInboxModel.set("BilledBySearchText", request.Get<string>("value"));
        store.SalesInboxModel = store.SalesInboxModel.set("BilledByFiltered", store.getSalesBilledByBasedOnSitesAndQuery());
    }

    private VisitFromDateChanged(request: LocalEvent, store: BillingStore): void {
        store.SalesInboxModel = store.SalesInboxModel.set("VisitFromDate", request.Get<Date>("value"));
    }

    private VisitToDateChanged(request: LocalEvent, store: BillingStore): void {
        store.SalesInboxModel = store.SalesInboxModel.set("VisitToDate", request.Get<Date>("value"));
    }
}

export { SalesSettingsContainerHandler };
